import { attr } from '@ember-data/model';
import { computed } from '@ember/object';
import { equal, or } from '@ember/object/computed';
import { isIn } from '@square/dashboard-utilities/macros';
import EmberDataBaseModel from '@square/dashboard-data/models/ember-data-base-model';
import { inject as service } from '@ember/service';
const VERIFICATION_TYPE = {
  VERIFICATION_IN_PROGRESS: 'verification_in_progress',
  VERIFICATION_CANCELED: 'verification_canceled',
  AWAITING_DEBIT_AUTHORIZATION: 'awaiting_debit_authorization',
  AWAITING_EMAIL_AUTHORIZATION: 'awaiting_email_authorization',
  AWAITING_EMAIL_CONFIRMATION: 'awaiting_email_confirmation',
  VERIFIED: 'verified',
  FAILED: 'failed'
};
const VERIFIED_STATES = [VERIFICATION_TYPE.VERIFIED];
const PENDING_STATES = [VERIFICATION_TYPE.VERIFICATION_IN_PROGRESS, VERIFICATION_TYPE.AWAITING_EMAIL_CONFIRMATION, VERIFICATION_TYPE.AWAITING_EMAIL_AUTHORIZATION, VERIFICATION_TYPE.AWAITING_DEBIT_AUTHORIZATION];
export default EmberDataBaseModel.extend({
  intl: service(),
  intlCountry: service(),
  fideliusToken: attr('string'),
  accountType: attr('bank-account-type', {
    defaultValue: 'checking'
  }),
  accountName: attr('string'),
  accountNumber: attr('string'),
  accountNumberSuffix: attr('string'),
  accountNumberConfirmation: attr('string'),
  bankName: attr('string'),
  routingNumber: attr('string'),
  verificationState: attr('string'),
  // Deprecated
  externalVerificationState: attr('string'),
  failureReason: attr('string'),
  // Only for failed accounts
  verifiedBy: attr('string'),
  unitToken: attr('string'),
  // Only for creating
  merchantToken: attr('string'),
  password: attr('string'),
  requiresDebitAuthorization: attr('boolean'),
  // CA only
  transitNumber: attr('string'),
  institutionNumber: attr('string'),
  // JP only
  jpBankCode: attr('string'),
  jpBranchCode: attr('string'),
  // AU only
  bsbNumber: attr('string'),
  // GB only
  sortCode: attr('string'),
  // FR only
  iban: attr('string'),
  swiftBic: attr('string'),
  isVerified: isIn('externalVerificationState', VERIFIED_STATES),
  isPending: isIn('externalVerificationState', PENDING_STATES),
  isAwaitingEmailConfirmation: equal('externalVerificationState', VERIFICATION_TYPE.AWAITING_EMAIL_CONFIRMATION),
  isAwaitingEmailAuthorization: equal('externalVerificationState', VERIFICATION_TYPE.AWAITING_EMAIL_AUTHORIZATION),
  isAwaitingUserApproval: or('isAwaitingEmailAuthorization', 'isAwaitingEmailConfirmation'),
  isVerificationStateCanceled: equal('externalVerificationState', VERIFICATION_TYPE.VERIFICATION_CANCELED),
  isVerificationStateFailed: equal('externalVerificationState', VERIFICATION_TYPE.FAILED),
  // GB, FR and AU do not have checking vs savings accounts so check this property before trying to display the bank account type
  hideAccountType: or('sortCode', 'bsbNumber', 'iban'),
  displayName: computed('accountNumberSuffix', 'accountType', 'bankName', 'hideAccountType', 'isPending', 'isVerificationStateCanceled', 'isVerificationStateFailed', function () {
    const properties = {
      bankName: this.bankName,
      accountType: this.accountType === 'investment' ? this.intlCountry.t(`business.bankAccount.accountTypes.${this.accountType}`) :
      // eslint-disable-next-line square/no-translation-key-interpolation
      this.intl.t(`business.bankAccount.accountTypes.${this.accountType}`),
      accountNumberSuffix: this.accountNumberSuffix
    };
    let verificationStateKey;
    if (this.isVerificationStateCanceled) {
      verificationStateKey = 'canceled';
    } else if (this.isVerificationStateFailed) {
      verificationStateKey = 'failed';
    } else if (this.isPending) {
      verificationStateKey = 'pending';
    }
    let label;
    if (!verificationStateKey) {
      label = this.hideAccountType ? 'accountLabelTypeless' : 'accountLabel';
    } else {
      // eslint-disable-next-line square/no-translation-key-interpolation
      properties['verificationState'] = this.intl.t(`locations.edit.locationSettings.bankAccount.states.${verificationStateKey}`);
      label = this.hideAccountType ? 'accountLabelTypelessWithState' : 'accountLabelWithState';
    }
    // eslint-disable-next-line square/no-translation-key-interpolation
    return this.intl.t(`locations.edit.locationSettings.bankAccount.${label}`, properties);
  })
}).reopenClass({
  VERIFICATION_TYPE,
  _modelNameForMigration: 'bank-account'
});